import React, { useState, useRef, useEffect } from 'react';
import WorkspaceSelector from '../Workspace/WorkspaceSelector';
import { getUnreadCount } from '../../utils/inboxStorage';
import { getAllWorkflows } from '../../utils/workflowEngine';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

const NAV_GROUPS = [
  {
    items: [
      { id: 'inbox',       label: 'Inbox',       icon: '📥', badge: true },
    ]
  },
  {
    label: 'Build',
    items: [
      { id: 'projects',    label: 'Projects',    icon: '📁' },
      { id: 'tables',      label: 'Data',        icon: '🗂️' },
      { id: 'pipeline',    label: 'Pipeline',    icon: '⬡' },
      { id: 'operations',  label: 'Operations',  icon: '🏢' },
      { id: 'agents',      label: 'Agents',      icon: '🤖', ai: true },
      { id: 'chats',       label: 'Chats',       icon: '💬', ai: true },
      { id: 'schedules',   label: 'Schedules',   icon: '⏱' },
      { id: 'tasks',       label: 'Tasks',       icon: '✓' },
      { id: 'connections', label: 'Connectors', icon: '⚡' },
      { id: 'support',     label: 'Support form', icon: '🎫' },
    ]
  },
  {
    label: 'Insights',
    items: [
      { id: 'logs',       label: 'Logs',           icon: '📋' },
      { id: 'reports',    label: 'Reports',        icon: '📊' },
      { id: 'docs',       label: 'Documentation',  icon: '📄' },
      { id: 'knowledge',  label: 'Knowledge Base', icon: '📚' },
    ]
  },
  {
    label: 'Learn',
    items: [
      { id: 'university', label: 'University', icon: '🎓' },
      { id: 'labs',       label: 'Labs',       icon: '🧪' },
    ]
  },
];

export default function Sidebar({
  activePage, onNavigate,
  workspace, workspaces, onWorkspaceChange, onCreateWorkspace, onDeleteWorkspace,
  user, userData, onSignOut,
  collapsed, onToggleCollapse,
  onBuzzOpen,
  mobileOpen, onMobileClose,
}) {
  const { aiEnabled } = useFeatureFlags();
  const showLabels = !collapsed || mobileOpen;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(() => getUnreadCount());
  const [activeWorkflows, setActiveWorkflows] = useState(() =>
    getAllWorkflows().filter(w => w.status === 'running' || w.status === 'paused').length
  );
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = () => setInboxUnread(getUnreadCount());
    window.addEventListener('projectly_inbox_update', handler);
    return () => window.removeEventListener('projectly_inbox_update', handler);
  }, []);

  useEffect(() => {
    const handler = () => setActiveWorkflows(
      getAllWorkflows().filter(w => w.status === 'running' || w.status === 'paused').length
    );
    window.addEventListener('projectly_workflow_update', handler);
    return () => window.removeEventListener('projectly_workflow_update', handler);
  }, []);

  useEffect(() => {
    if (!showUserMenu) return;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showUserMenu]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('sidebar-open-lock');
    } else {
      document.body.classList.remove('sidebar-open-lock');
    }
    return () => document.body.classList.remove('sidebar-open-lock');
  }, [mobileOpen]);

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={onMobileClose} aria-hidden="true" />
      )}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {mobileOpen && (
          <button className="sidebar-mobile-close" onClick={onMobileClose} aria-label="Close menu">
            ✕
          </button>
        )}
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="Projectly" className="sidebar-logo-img" />
            {showLabels && <span className="sidebar-logo-text">Projectly</span>}
          </div>

          {showLabels && (
            <WorkspaceSelector
              workspace={workspace}
              workspaces={workspaces}
              onSelect={onWorkspaceChange}
              onCreate={onCreateWorkspace}
              onDelete={onDeleteWorkspace}
            />
          )}

          <nav className="sidebar-nav">
            {NAV_GROUPS.map((group, gi) => (
              <div key={gi} className="sidebar-nav-group">
                {group.label && showLabels && (
                  <div className="sidebar-nav-group-label">{group.label}</div>
                )}
                {gi > 0 && <div className="sidebar-nav-divider" />}
                {group.items.map(item => {
                  const disabled = item.ai && !aiEnabled;
                  const badgeCount = item.badge ? inboxUnread : item.id === 'tasks' ? activeWorkflows : 0;
                  return (
                    <button
                      key={item.id}
                      data-testid={`nav-${item.id}`}
                      className={`sidebar-nav-item ${activePage === item.id ? 'active' : ''}${disabled ? ' disabled' : ''}`}
                      onClick={() => !disabled && onNavigate(item.id)}
                      title={disabled ? `${item.label} (AI features disabled)` : (!showLabels ? item.label : undefined)}
                      style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <span className="sidebar-nav-icon" style={{ position: 'relative' }}>
                        {item.icon}
                        {badgeCount > 0 && (
                          <span className="sidebar-inbox-badge">{badgeCount}</span>
                        )}
                      </span>
                      {showLabels && <span className="sidebar-nav-label">{item.label}</span>}
                      {showLabels && item.badgeText && <span className="sidebar-nav-tag">{item.badgeText}</span>}
                      {showLabels && badgeCount > 0 && (
                        <span className="sidebar-inbox-badge-label">{badgeCount}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <button
            className={`sidebar-nav-item buzz-btn ${activePage === 'buzz' ? 'active' : ''}${!aiEnabled ? ' disabled' : ''}`}
            onClick={() => aiEnabled && onBuzzOpen()}
            title={!aiEnabled ? 'Buzz AI (AI features disabled)' : (!showLabels ? 'Buzz AI' : undefined)}
            style={!aiEnabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
          >
            <span className="sidebar-nav-icon">🐝</span>
            {showLabels && <span className="sidebar-nav-label">Buzz AI</span>}
          </button>
        </div>

        <div className="sidebar-bottom">
          {showLabels && (
            <div className="sidebar-user-menu-wrap" ref={menuRef}>
              {showUserMenu && (
                <div className="sidebar-user-menu">
                  <button className="sidebar-user-menu-item" onClick={() => { onNavigate('settings'); setShowUserMenu(false); }}>
                    <span>⚙</span> Settings
                  </button>
                  <button className="sidebar-user-menu-item" onClick={() => { onNavigate('workspaceSettings'); setShowUserMenu(false); }}>
                    <span>🏢</span> Workspace settings
                  </button>
                  <button className="sidebar-user-menu-item danger" onClick={onSignOut}>
                    <span>↩</span> Sign out
                  </button>
                </div>
              )}
              <div className="sidebar-user" onClick={() => setShowUserMenu(v => !v)}>
                <div className="sidebar-user-name">{userData?.username || user.email.split('@')[0]}</div>
                <div className="sidebar-user-email">{user.email}</div>
              </div>
            </div>
          )}
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>
      </aside>
    </>
  );
}
